import { forwardRef, lazy, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
    Card,
    CardOpacity,
    NameStyle,
    NameStyleType,
    FrameInfoMap,
    FinishMap,
    CardFormatMode,
    getCardFormatMode,
} from '../../model';
import {
    Affiliation,
    AppHeader,
    IconButton,
    RadioTrain,
    StandaloneLabel,
    StyledPopMarkdown,
} from '../../component';
import {
    checkMonster,
} from '../../util';
import { ClearOutlined } from '@ant-design/icons';
import { CharPicker } from './char-picker';
import { type NameStylePickerRef } from './name-style-picker';
import { CheckboxTrain, FrameTrain, FrameTrainRef } from './input-train';
import { Explanation } from 'src/component/explanation';
import { changeCardFormat, useCard, useLanguage, useSetting } from '../../service';
import { LayoutPicker, OpacityPickerRef } from './layout-picker';
import {
    EffectInputGroup,
    EffectInputGroupRef,
    FooterInputGroup,
    FooterInputGroupRef,
    ImageInputGroup,
    ImageInputGroupRef,
    NameSetInputGroup,
    NameSetInputGroupRef,
    PendulumInputGroup,
    PendulumInputGroupRef,
    PostPendulumInputGroup,
    PostPendulumInputGroupRef,
} from './input-group';
import {
    TextStylePicker,
} from './text-style-picker';
import { useShallow } from 'zustand/react/shallow';
import {
    StyledFormatRadioTrain,
    StyledInputLabelWithButton,
    StyledNameSetIdInputContainer,
} from './input-panel.styled';
import { IconInputGroup, IconInputGroupRef } from './icon-input-group';
import './input-panel.scss';
import { getFoilButtonList } from './const';
import { AttributeInputGroup, AttributeInputGroupRef } from './attribute-input-group';

const NameStylePicker = lazy(() => import('./name-style-picker').then(({ NameStylePicker }) => ({ default: NameStylePicker })));
const FormatButtonList = [
    {
        label: '简中',
        value: 'sc',
        props: {
            className: 'sc-button',
            style: { fontWeight: 'bold' } as React.CSSProperties
        },
    },
    {
        label: 'OCG',
        value: 'ocg',
        props: {
            className: 'ocg-button',
            style: { fontWeight: 'bold' } as React.CSSProperties
        },
    },
    {
        label: 'TCG',
        value: 'tcg',
        props: {
            className: 'tcg-button',
            style: { fontWeight: 'bold' } as React.CSSProperties
        },
    },
];
export type CardInputPanelRef = {
    forceCardData: (card: Card) => void,
    isLoading: () => boolean,
};
export type CardInputPanel = {
    artworkCanvas: ImageInputGroup['receivingCanvas'],
    attributeCanvas: ImageInputGroup['receivingCanvas'],
    backgroundCanvas: ImageInputGroup['receivingCanvas'],
    iconImageCanvas: ImageInputGroup['receivingCanvas'],
    overlayCanvas: ImageInputGroup['receivingCanvas'],
} & Pick<ImageInputGroup, 'onCropChange' | 'onTainted' | 'onSourceLoaded'> & Pick<AppHeader, 'applyCardData' | 'resetCardData'>;
export const CardInputPanel = forwardRef<CardInputPanelRef, CardInputPanel>(({
    resetCardData,
    applyCardData,
    artworkCanvas,
    backgroundCanvas,
    overlayCanvas,
    iconImageCanvas,
    attributeCanvas,
    onCropChange,
    onTainted,
    onSourceLoaded,
}: CardInputPanel, forwardedRef) => {
    const language = useLanguage();
    const {
        format,
        region,
        frame, foil, finish, opacity,
        nameStyleType, nameStyle,
        getUpdater,
        setCard,
    } = useCard(useShallow(({
        card: {
            format, region,
            frame, foil, finish, opacity,
            nameStyleType, nameStyle,
            isLink,
        },
        getUpdater,
        setCard,
    }) => ({
        format, region,
        frame, foil, finish, opacity,
        nameStyleType, nameStyle,
        isLink,
        getUpdater,
        setCard,
    })));
    const { setting } = useSetting();
    const { showCreativeOption, showExtraDecorativeOption, reduceMotionColor } = setting;

    const stylePickerRef = useRef<NameStylePickerRef>(null);

    const isMonster = checkMonster({ frame });
    const [stylePickerResetCount, setStylePickerResetCount] = useState(0);
    const foilButtonList = useMemo(() => getFoilButtonList(language), [language]);
    const formatMode = getCardFormatMode(format, region);

    const frameTrainRef = useRef<FrameTrainRef>(null);
    const attributeInputGroupRef = useRef<AttributeInputGroupRef>(null);
    const iconInputGroupRef = useRef<IconInputGroupRef>(null);
    const imageInputGroupRef = useRef<ImageInputGroupRef>(null);
    const layoutPickerRef = useRef<OpacityPickerRef>(null);
    const nameSetIdInputGroupRef = useRef<NameSetInputGroupRef>(null);
    const pendulumInputGroupRef = useRef<PendulumInputGroupRef>(null);
    const effectInputGroupRef = useRef<EffectInputGroupRef>(null);
    const postPendulumInputGroupRef = useRef<PostPendulumInputGroupRef>(null);
    const footerInputGroupRef = useRef<FooterInputGroupRef>(null);

    const [pickerTarget, setPickerTarget] = useState<{ id: string, setValue: (nextValue: string) => void }>({
        id: '',
        setValue: () => { },
    });

    const changeFormat = (formatValue: string | number) => {
        setCard(currentCard => {
            const nextFormat = `${formatValue}` as CardFormatMode;
            const formatSwappedCard = changeCardFormat(currentCard, nextFormat);

            const { name, setId, effect, typeAbility, password, creator, pendulumEffect } = formatSwappedCard;
            nameSetIdInputGroupRef.current?.setValue({ name, setId });
            effectInputGroupRef.current?.setValue(effect);
            pendulumInputGroupRef.current?.setValue({ pendulumEffect });
            postPendulumInputGroupRef.current?.setValue({ typeAbility });
            footerInputGroupRef.current?.setValue({ creator, password });

            return formatSwappedCard;
        });
    };
    const changeFoil = useMemo(() => getUpdater('foil'), [getUpdater]);
    const onFinishChange = useMemo(() => getUpdater('finish'), [getUpdater]);
    const changeOpacity = useCallback((opacity: CardOpacity) => setCard(curr => ({ ...curr, opacity })), [setCard]);
    const changeNameStyle = useCallback((type: NameStyleType, value: Partial<NameStyle>) => {
        setCard(currentCard => {
            return {
                ...currentCard,
                nameStyleType: type,
                nameStyle: value,
            };
        });
    }, [setCard]);

    const finishList = useMemo(
        () => Object.values(FinishMap).map(({ value, label, labelKey, tooltipKey }) => ({
            label: labelKey ? language[labelKey] : label,
            tooltip: tooltipKey ? language[tooltipKey] : undefined,
            value,
        })),
        [language],
    );

    useEffect(() => {
        stylePickerRef.current?.setValue({ font: nameStyle.font });
    }, [nameStyle]);

    useEffect(() => {
        layoutPickerRef.current?.setValue(opacity);
    }, [opacity]);

    useImperativeHandle(forwardedRef, () => ({
        /** @todo Maybe we need to check foil loading too. */
        isLoading: () => (imageInputGroupRef.current?.isLoading() ?? false)
            || (layoutPickerRef.current?.isLoading() ?? false)
            || (iconInputGroupRef.current?.isLoading() ?? false),
        forceCardData: card => {
            setStylePickerResetCount(cnt => cnt + 1);
            const {
                name,
                art, artCrop, artData, artSource,
                background, backgroundCrop, backgroundData, backgroundSource,
                iconImage, iconImageCrop, iconImageData, iconImageSource,
                overlay, overlayCrop, overlayData, overlaySource,
                opacity,
                attribute, attributeImageSource, attributeImage, attributeImageCrop, attributeImageData,
                setId,
                pendulumEffect,
                typeAbility,
                effect,
                atk, def,
                creator, password,
                effectStyle, pendulumStyle,
                firstEditionText,
                cornerText,
            } = card;

            imageInputGroupRef.current?.setValue({ art, artCrop, artData, artSource });
            layoutPickerRef.current?.setValue({
                ...opacity,
                background, backgroundCrop, backgroundData, backgroundSource,
            });
            attributeInputGroupRef.current?.setValue({
                attribute, attributeImageSource, attributeImage, attributeImageCrop, attributeImageData,
            });
            nameSetIdInputGroupRef.current?.setValue({ name, setId });
            pendulumInputGroupRef.current?.setValue({
                pendulumEffect,
                overlay, overlayCrop, overlayData, overlaySource,
            });
            iconInputGroupRef.current?.setValue({
                iconImage, iconImageCrop, iconImageData, iconImageSource,
            });
            effectInputGroupRef.current?.setValue(effect);
            postPendulumInputGroupRef.current?.setValue({
                typeAbility,
                effectMinLine: effectStyle.minLine,
                pendulumEffectMinLine: pendulumStyle.minLine,
                effectJustifyRatio: effectStyle.justifyRatio,
                pendulumEffectJustifyRatio: pendulumStyle.justifyRatio,
            });
            footerInputGroupRef.current?.setValue({ atk, def, creator, password, firstEditionText, cornerText });
        }
    }));

    return <div
        className={['card-info-panel', format === 'ocg' ? 'input-ocg' : 'input-tcg'].join(' ')}
    >
        <AppHeader
            resetCardData={resetCardData}
            applyCardData={applyCardData}
        />
        <br />
        <Affiliation />

        <div className="card-overlay-input">
            <StyledFormatRadioTrain className="format-radio" value={formatMode} onChange={changeFormat} optionList={FormatButtonList}>
                <span>{language['input.format.label']}</span>
            </StyledFormatRadioTrain>
            <RadioTrain className="foil-radio" value={foil} onChange={changeFoil} optionList={foilButtonList}>
                <span>{language['input.foil.label']}</span>
            </RadioTrain>
            {showExtraDecorativeOption && <CheckboxTrain
                className="finish-checkbox"
                value={finish}
                onChange={onFinishChange}
                optionList={finishList}
            >
                <StyledInputLabelWithButton className="finish-checkbox-label">
                    <div className="input-label">{language['input.finish.label']}</div>
                    <IconButton
                        onClick={() => onFinishChange([])}
                        Icon={ClearOutlined}
                        tooltipProps={{
                            title: language['input.other-finish.reset.tooltip'],
                        }}
                    />
                </StyledInputLabelWithButton>
            </CheckboxTrain>}
        </div>

        {showCreativeOption && <div className="card-layout-input">
            <label className="standalone-addon ant-input-group-addon">
                {language['input.layout.label']} <Explanation
                    content={<StyledPopMarkdown>{language['input.layout.tooltip']}</StyledPopMarkdown>}
                />
            </label>
            <LayoutPicker ref={layoutPickerRef}
                defaultValue={opacity}
                receivingCanvas={backgroundCanvas}
                onChange={changeOpacity}
                onTainted={onTainted}
                onCropChange={onCropChange}
                onSourceLoaded={onSourceLoaded}
            />
        </div>}

        <FrameTrain ref={frameTrainRef}
            onSTFrameChange={typeAbility => postPendulumInputGroupRef.current?.setValue({ typeAbility })}
            onPasswordChange={password => footerInputGroupRef.current?.setValue({ password })}
            onStatChange={(atk, def, linkRating) => footerInputGroupRef.current?.setValue({ atk, def, linkRating })}
        />

        <StyledNameSetIdInputContainer className="name-style-id-input">
            <NameSetInputGroup ref={nameSetIdInputGroupRef}
                onTakePicker={setPickerTarget}
            />
            <Suspense fallback={<div className="name-style-picker-placeholder" />}>
                <NameStylePicker key={stylePickerResetCount} ref={stylePickerRef}
                    frameInfo={FrameInfoMap[frame as keyof typeof FrameInfoMap]}
                    defaultType={nameStyleType}
                    defaultValue={nameStyle}
                    showExtraDecorativeOption={showExtraDecorativeOption}
                    onChange={changeNameStyle}
                />
            </Suspense>
            <IconInputGroup ref={iconInputGroupRef}
                showCreativeOption={showCreativeOption}
                receivingCanvas={iconImageCanvas}
                onTainted={onTainted}
                onSourceLoaded={onSourceLoaded}
            />
        </StyledNameSetIdInputContainer>
        <div className="main-info">
            <div className="main-info-first">
                <AttributeInputGroup ref={attributeInputGroupRef}
                    language={language}
                    receivingCanvas={attributeCanvas}
                    onTainted={onTainted}
                    onSourceLoaded={onSourceLoaded}
                    onCropChange={onCropChange}
                />

                <PendulumInputGroup ref={pendulumInputGroupRef}
                    showCreativeOption={showCreativeOption}
                    showExtraDecorativeOption={showExtraDecorativeOption}
                    softMode={reduceMotionColor}
                    receivingCanvas={overlayCanvas}
                    onTainted={onTainted}
                    onSourceLoaded={onSourceLoaded}
                    onCropChange={onCropChange}
                    onTakePicker={setPickerTarget}
                    onFrameChange={frame => frameTrainRef.current?.changeFrame(frame)}
                />

                <PostPendulumInputGroup ref={postPendulumInputGroupRef} onTakePicker={setPickerTarget} />

                <div>
                    <div className="card-effect-letter-helper">
                        <StandaloneLabel className="standalone-label">
                            {language['input.effect.label']}
                        </StandaloneLabel>
                        {showCreativeOption ? <TextStylePicker /> : <div />}
                        <CharPicker
                            targetId={pickerTarget.id}
                            onPick={pickerTarget.setValue}
                        />
                    </div>
                    <EffectInputGroup ref={effectInputGroupRef} onTakePicker={setPickerTarget} />
                </div>

                <FooterInputGroup ref={footerInputGroupRef}
                    isMonster={isMonster}
                    showCreativeOption={showCreativeOption}
                    onTakePicker={setPickerTarget}
                />
            </div>
            <div className="main-info-second">
                <ImageInputGroup ref={imageInputGroupRef}
                    receivingCanvas={artworkCanvas}
                    showCreativeOption={showCreativeOption}
                    showExtraDecorativeOption={showExtraDecorativeOption}
                    onSourceLoaded={onSourceLoaded}
                    onTainted={onTainted}
                    onCropChange={onCropChange}
                />
            </div>
        </div>
    </div>;
});
